import { fetchDemCustomer } from './demCustomers';
import { formatSupabaseError } from './formatSupabaseError';
import { getDbSupabase } from './supabase';

export type DemCloudTalkLinkedCustomer = {
  code: string;
  name: string;
  status: string;
  isBlocked: boolean;
};

export type DemCloudTalkLinkedOrder = {
  orderId: string;
  customerCode: string;
  customerName: string;
  source: string;
  status: string;
  createdAt: string;
  createdAtDisplay: string;
  contactNo: string;
};

export type DemCloudTalkPhoneMatch = {
  phoneKey: string;
  customers: DemCloudTalkLinkedCustomer[];
  orders: DemCloudTalkLinkedOrder[];
};

/** Stable SG-oriented key: last 8 national digits. */
export function phoneMatchKey(raw: string): string {
  const digits = raw.trim().replace(/\D/g, '');
  if (!digits) return '';
  if (digits.startsWith('65') && digits.length >= 10) return digits.slice(-8);
  if (digits.length >= 8) return digits.slice(-8);
  return digits;
}

function formatCreatedAt(iso: string): string {
  const raw = iso.trim();
  if (!raw) return '—';
  try {
    const d = new Date(raw);
    if (Number.isNaN(d.getTime())) return raw.slice(0, 16).replace('T', ' ');
    return d.toLocaleString('en-SG', {
      timeZone: 'Asia/Singapore',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    });
  } catch {
    return raw.slice(0, 16).replace('T', ' ');
  }
}

/**
 * Link a CloudTalk caller number to DEM customers/orders via orders.contact_no.
 * DEM customers master has no phone column — order contact numbers are the bridge.
 */
export async function lookupDemByCallerNumber(
  callerNumber: string,
): Promise<DemCloudTalkPhoneMatch | null> {
  const phoneKey = phoneMatchKey(callerNumber);
  if (phoneKey.length < 8) return null;

  const db = getDbSupabase('dem');
  const { data, error } = await db
    .from('orders')
    .select('order_id, customer_code, contact_no, source, status, created_at')
    .ilike('contact_no', `%${phoneKey}%`)
    .neq('status', 'Archived')
    .order('created_at', { ascending: false })
    .limit(50);
  if (error) throw new Error(formatSupabaseError(error));

  const matched = (data ?? []).filter((row) => {
    const key = phoneMatchKey(String((row as { contact_no?: string }).contact_no ?? ''));
    return key === phoneKey;
  }) as Array<{
    order_id?: string;
    customer_code?: string;
    contact_no?: string;
    source?: string;
    status?: string;
    created_at?: string;
  }>;

  if (!matched.length) {
    return { phoneKey, customers: [], orders: [] };
  }

  const codes = [
    ...new Set(matched.map((row) => String(row.customer_code ?? '').trim()).filter(Boolean)),
  ];
  const customers: DemCloudTalkLinkedCustomer[] = [];
  const nameByCode = new Map<string, string>();
  await Promise.all(
    codes.map(async (code) => {
      try {
        const customer = await fetchDemCustomer(code);
        if (!customer) {
          nameByCode.set(code, code);
          return;
        }
        nameByCode.set(code, customer.name || code);
        customers.push({
          code: customer.code,
          name: customer.name || code,
          status: customer.status || '',
          isBlocked: Boolean(customer.is_blocked),
        });
      } catch {
        nameByCode.set(code, code);
      }
    }),
  );

  customers.sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }));

  const orders: DemCloudTalkLinkedOrder[] = matched.slice(0, 12).map((row) => {
    const customerCode = String(row.customer_code ?? '').trim();
    const createdAt = String(row.created_at ?? '');
    return {
      orderId: String(row.order_id ?? ''),
      customerCode,
      customerName: nameByCode.get(customerCode) || customerCode || '—',
      source: String(row.source ?? ''),
      status: String(row.status ?? ''),
      createdAt,
      createdAtDisplay: formatCreatedAt(createdAt),
      contactNo: String(row.contact_no ?? ''),
    };
  });

  return { phoneKey, customers, orders };
}

/**
 * Cheap presence check for call-list badges: which caller keys have any DEM order.
 * Returns last-8-digit keys that match at least one non-archived order.
 */
export async function findDemLinkedPhoneKeys(callerNumbers: string[]): Promise<Set<string>> {
  const keys = [
    ...new Set(
      callerNumbers
        .map((n) => phoneMatchKey(n))
        .filter((key) => key.length >= 8),
    ),
  ];
  if (!keys.length) return new Set();

  const db = getDbSupabase('dem');
  const orFilter = keys.map((key) => `contact_no.ilike.%${key}%`).join(',');
  const { data, error } = await db
    .from('orders')
    .select('contact_no')
    .or(orFilter)
    .neq('status', 'Archived')
    .limit(200);
  if (error) throw new Error(formatSupabaseError(error));

  const hit = new Set<string>();
  const keySet = new Set(keys);
  for (const row of data ?? []) {
    const key = phoneMatchKey(String((row as { contact_no?: string }).contact_no ?? ''));
    if (keySet.has(key)) hit.add(key);
  }
  return hit;
}
